import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { PhoneFrame, ScreenMatch, ScreenPlanner } from './PhoneMockup';
import { StoreBadges } from './StoreBadges';

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToCta = () => {
    document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#FFFDF8] pt-10 md:pt-16 pb-20 md:pb-28"
    >
      {/* radial accent */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-orange-200/60 to-amber-100/0 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 h-[420px] w-[420px] rounded-full bg-gradient-to-tr from-rose-100/60 to-transparent blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-75" />
              <span className="relative rounded-full bg-orange-500 h-2 w-2" />
            </span>
            {t.hero.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display font-extrabold tracking-tight text-stone-900 text-balance"
            style={{ lineHeight: 0.95 }}
            data-testid="hero-title"
          >
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem]">
              {t.hero.titleLine1}
            </span>
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem]">
              <span className="relative inline-block">
                <span className="relative z-10 text-orange-500">{t.hero.titleLine2}</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="14"
                  viewBox="0 0 300 14"
                  preserveAspectRatio="none"
                  fill="none"
                >
                  <path
                    d="M2 9 C 80 1, 180 14, 298 6"
                    stroke="#F97316"
                    strokeWidth="4"
                    strokeLinecap="round"
                    opacity="0.6"
                  />
                </svg>
              </span>
            </span>
          </motion.h1>

          {/* scribble accent */}
          <div className="relative mt-4 mb-6 ml-2 sm:ml-6 inline-block">
            <span className="font-accent text-2xl md:text-3xl text-orange-600 -rotate-3 inline-block">
              {t.hero.scribble}
            </span>
            <svg
              className="absolute -right-8 -top-3 hidden sm:block"
              width="40"
              height="30"
              viewBox="0 0 40 30"
              fill="none"
            >
              <path
                d="M2 28 C 10 15, 20 5, 36 2 M28 2 L36 2 L34 10"
                stroke="#F97316"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="max-w-xl text-base md:text-lg text-stone-600 leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={scrollToCta}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-stone-800 active:scale-95 transition-all shadow-lg"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={scrollToFeatures}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-stone-200 text-stone-900 text-sm font-bold hover:border-stone-900 active:scale-95 transition-all"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          {/* coming soon badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8"
          >
            <StoreBadges theme="light" testIdPrefix="hero-store" />
          </motion.div>

          {/* mini stats */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-stone-700">{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — phones */}
        <div className="lg:col-span-5 relative h-[520px] sm:h-[600px] flex items-center justify-center">
          {/* food image accent */}
          <div className="absolute top-10 right-0 h-28 w-28 rounded-full overflow-hidden border-8 border-white shadow-lg rotate-12 hidden sm:block animate-float">
            <img
              src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=300&q=80"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute bottom-12 left-0 h-24 w-24 rounded-full overflow-hidden border-8 border-white shadow-lg -rotate-12 hidden sm:block animate-float-slow">
            <img
              src="https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=300&q=80"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -10 }}
            animate={{ opacity: 1, y: 0, rotate: -7 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="absolute left-0 sm:left-4 lg:-left-8 top-16 z-10 scale-[0.78] sm:scale-90"
          >
            <PhoneFrame testId="hero-phone-planner">
              <ScreenPlanner />
            </PhoneFrame>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 8 }}
            animate={{ opacity: 1, y: 0, rotate: 4 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="relative z-20 scale-90 sm:scale-100 translate-x-12 sm:translate-x-16"
          >
            <PhoneFrame testId="hero-phone-match">
              <ScreenMatch />
            </PhoneFrame>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
